/* eslint-disable import/no-unresolved */
import { Dispatch, useEffect, useMemo, useRef, useState } from 'react';
import TagManager from 'react-gtm-module';
import { connect, ConnectedProps } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import classNames from 'classnames';
import { Action } from 'redux';

import { PaymentPspStatusEnum } from './lib/enums/payments/payment-psp-status.enum';
import lang from './lib/language';
import { IGNORE_GATEWAY_CONNECTION } from './lib/libSettings';
import { ENROLL_AUTH_REQUEST_TYPE } from './lib/models/enroll-requests';
import { CardTransferStatusSuccessParam } from './lib/models/payment/types';
import { RootState } from './lib/store';
import { getAllMessages } from './lib/store/ams';
import { appConnected, setOnlineStatus, setWebLoginReady } from './lib/store/app';
import {
  applicationStartup,
  gatewayConnected,
  login,
  logout,
  reconnectEventAction,
} from './lib/store/common-actions';
import { setPendingPurchasedPlanId } from './lib/store/crm';
import { getCurrentAccount, hasAnyAccountPaidPlan } from './lib/store/crm/helpers';
import { CRMCall } from './lib/store/crm/types';
import { getCardTransferStatus } from './lib/store/payment';
import { isTradingPlanPaymentProcessed } from './lib/store/payment/helpers';
import { PaymentCall } from './lib/store/payment/types';
import {
  hasValidCookie,
  isCookieCheckPending,
  isLoginPending as storeIsLoginPending,
  isUserBeforeForVerificationStatus,
  isUserEnrollStepForVerification,
  isUserVerifiedStatus,
} from './lib/store/selectors';
import GatewayWebsocketState from './lib/store-util/GatewayWebsocketState';
import {
  isCallStatusError,
  isCallStatusInitial,
  isCallStatusPending,
  isCallStatusReady,
} from './lib/util/error-handling/StatusByCallHelpers';
import {
  getPaymentProvider,
  getPaymentReferenceId,
  getPurchasedPlanId,
  isPaymentSuccessCallback,
  markPlanPurchasePaymentCallback,
} from './lib/util/url-helpers';

import { GTM_CATEGORIES, GTM_EVENTS, GTM_LOGIN } from './constants/gtm';
import { hashSHA256 } from './util/hash';
import { preparePaymentErrorPathByPaymentProvider, startMockedMode } from './util/helpers';
import { isFullScreenRoute } from './util/layout.helpers';
import { UI_ROUTE_MOCKED, UI_ROUTES } from './util/routes';
import { getRouteMatches } from './util/routes.helpers';
import {
  CloseoutManager,
  DebugPanels,
  MaintenanceNoticeBar,
  ModalsManager,
  NotificationsManager,
  Preloader,
  ScrollToTop,
} from './components';
import { MOCK, setLogConfig } from './configDebug';
import configLib from './configLib';
import HeaderAndSideBar from './HeaderAndSideBar';
import { useIsMobile } from './hooks';
import { ANONYMOUS_ROUTES, type AuthenticatedRedirect, getAuthenticatedRoutes, RoutesRenderer } from './routes';
import useNetwork from './useNetwork';

import styles from './App.module.scss';

type Props = ReduxProps;

const App = (props: Props): JSX.Element => {
  const ref = useRef({ appLoaded: false, path: '' });

  const {
    start,
    isOnline,
    isForceRedirectToEnroll,
    isAuthenticated,
    isAppLoaded,
    isCookieValid,
    isPhoneConfirmed,
    isUserStatusForVerification,
    loginResultCallback,
    setLoginReady,
    isExtendedInfoReady,
    isLoginPending,
    isAppConnected,
    currentAccount,
    isUserVerified,
    getAllDataMessages,
    appLogOut,
    setNetworkStatus,
    reconnectToGwAndSubscibeSymbols,
    notifyAppConnected,
    notifyGatewayConnected,
    loginRedirect,
    phoneNumber,
    phoneCode,
    getPlanPaymentStatus,
    lastCardTransferStatus,
    isPlanPaymentProcessed,
    isCardTransferStatusInitial,
    isCardTransferStatusPending,
    isActivatingDemoAccount,
    isActivatedDemoAccount,
    isCardTransferStatusReady,
    isCardTransferStatusError,
    isTradingPlanReady,
    hasPaidPlan,
    isUserStatusBeforeForVerification,
    autoLoginSuccessURL,
    setPurchasedPlanId,
    userId,
    userEmail,
    isCookieSessionCheckPending,
    isRedeemSignupTokenFlow,
  } = props;

  const { isMobile } = useIsMobile();
  const {
    isConnected: isGWConnected,
    isLoggedin: isGWLoggedin,
    isConnectionTimedOut: isGatewayConnectionTimedOut,
  } = GatewayWebsocketState.use();

  const navigate = useNavigate();
  const location = useLocation();
  const { pathname, search, state } = location;

  const networkStatus = useNetwork();

  useEffect(() => {
    if (isGWConnected && isGWLoggedin) {
      notifyGatewayConnected();
      notifyAppConnected();
    }
  }, [isGWConnected, isGWLoggedin, notifyAppConnected, notifyGatewayConnected]);

  useEffect(() => {
    if (networkStatus && !isOnline) {
      reconnectToGwAndSubscibeSymbols();
    }
    setNetworkStatus(networkStatus);
  }, [isOnline, networkStatus, reconnectToGwAndSubscibeSymbols, setNetworkStatus]);

  const [isRedirected, setRedirected] = useState<boolean>(false);
  const [isLoadedApp, setIsLoadedApp] = useState<boolean>(false);
  const [isRedirectLogin, setIsRedirectLogin] = useState<boolean>(false);
  const [isSkipPlanPayment, setIsSkipPlanPayment] = useState<boolean>(false);
  const [paymentStatusAttempts, setPaymentStatusAttempts] = useState<number>(0);
  const hasTrackedLoginSuccess = useRef<boolean>(false);

  const searchParams = new URLSearchParams(window.location.search);
  const isPaymentSuccessReturn = useMemo(() => isPaymentSuccessCallback(), []);
  const isPlanPurchaseReturn = useMemo(() => markPlanPurchasePaymentCallback(), []);
  const paymentReferenceId = useMemo(() => getPaymentReferenceId(), []);
  const paymentProvider = useMemo(() => getPaymentProvider(), []);
  const purchasedPlanId = useMemo(() => getPurchasedPlanId(), []);
  const isDirectLogin = searchParams.get('directLogin');
  if (isDirectLogin && !isRedirectLogin) {
    setIsRedirectLogin(true);
  }

  useEffect(() => {
    if (isRedirectLogin && !isAuthenticated && isAppLoaded) {
      loginRedirect();
    }
  }, [isRedirectLogin, isAppLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  const matches = useMemo(() => getRouteMatches(pathname), [pathname]);

  const {
    isAuthResultPath,
    isEnrollPhoneVerified,
    isPaymentPlanErrorPath,
    isMockedPath,
    isEmailConfirmPath,
    isTradingViewPath,
    isErrorPath,
  } = matches;

  const isFullScreen = useMemo(() => isFullScreenRoute({ matches, isMobile }), [matches, isMobile]);

  const emailConfirmationOrResentWhileLoggedIn = isEmailConfirmPath && isCookieValid && !isRedeemSignupTokenFlow;

  const worker = useMemo(
    () => new Worker(new URL('./worker.ts', import.meta.url), { type: 'module' }),
    [],
  );

  useEffect(() => {
    configLib.worker = worker;
  }, [worker]);

  // did mount
  useEffect(() => {
    if (ref.current.appLoaded) return;
    ref.current.appLoaded = true;

    if (emailConfirmationOrResentWhileLoggedIn) {
      appLogOut({ preserveEnrollUserId: true });
      return;
    }
    if (pathname.includes(UI_ROUTE_MOCKED) && !MOCK.ENABLED) {
      startMockedMode();
      navigate(pathname, { replace: true });
    } else {
      start();
      getAllDataMessages();
    }

    if (search.includes('debug=')) {
      const queryParams = new URLSearchParams(search);
      setLogConfig(queryParams.get('debug'));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isGWConnected && !IGNORE_GATEWAY_CONNECTION) {
      setRedirected(false);
    }
  }, [isGWConnected]);

  useEffect(() => {
    if (purchasedPlanId && isPaymentSuccessReturn) {
      setPurchasedPlanId(purchasedPlanId);
    }
  }, [purchasedPlanId, isPaymentSuccessReturn, setPurchasedPlanId]);

  useEffect(() => {
    if (
      isAppLoaded
      && isPaymentSuccessReturn
      && paymentReferenceId
      && paymentProvider
      && isCardTransferStatusInitial
      && !isCardTransferStatusError
    ) {
      getPlanPaymentStatus({ paymentReference: paymentReferenceId, paymentProvider });
      setPaymentStatusAttempts(1);
    }
  }, [
    // eslint-disable-line react-hooks/exhaustive-deps
    isAppLoaded,
    isPaymentSuccessReturn,
    isCardTransferStatusInitial,
    isCardTransferStatusError,
    paymentReferenceId,
    paymentProvider,
  ]);

  // Poll payment status a few times on success return to handle async provider callbacks
  useEffect(() => {
    if (
      !isPaymentSuccessReturn
      || !paymentReferenceId
      || !paymentProvider
      || isCardTransferStatusError
      || isPlanPaymentProcessed
    ) {
      return () => {};
    }

    const delays = [3000, 4000]; // 3s, 4s
    const pspStatus = lastCardTransferStatus?.status as PaymentPspStatusEnum | undefined;
    const isPspPending = pspStatus === PaymentPspStatusEnum.ProcessingByPsp;
    const isPspProcessed = pspStatus === PaymentPspStatusEnum.Processed;
    const needsAnotherTry = paymentStatusAttempts > 0
      && paymentStatusAttempts < delays.length
      && !isPspProcessed
      && (!isCardTransferStatusReady || isPspPending || !pspStatus);

    let timer: NodeJS.Timeout | undefined;
    if (needsAnotherTry) {
      const delay = delays[paymentStatusAttempts - 1];
      timer = setTimeout(() => {
        getPlanPaymentStatus({ paymentReference: paymentReferenceId, paymentProvider });
        setPaymentStatusAttempts((prev) => prev + 1);
      }, delay);
    }

    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [
    isPaymentSuccessReturn,
    paymentReferenceId,
    paymentProvider,
    isCardTransferStatusReady,
    isCardTransferStatusError,
    isPlanPaymentProcessed,
    paymentStatusAttempts,
    lastCardTransferStatus,
    getPlanPaymentStatus,
  ]);

  // Reset payment status retries on new success return params
  useEffect(() => {
    setPaymentStatusAttempts(0);
  }, [paymentReferenceId, paymentProvider]);

  /**
   * The following two useEffect are for handling auth-result
   * */
  useEffect(() => {
    if (isAppLoaded && isAuthResultPath) {
      loginResultCallback();
    }
  }, [loginResultCallback, isAppLoaded, isAuthResultPath]);

  useEffect(() => {
    if (state?.skipPaymentEnrollment) {
      setIsSkipPlanPayment(true);
    }
  }, [state?.skipPaymentEnrollment]);

  const hasSession = location.search.includes('session_state=');

  useEffect(() => {
    if (
      MOCK.ENABLED
      && !pathname.includes(UI_ROUTE_MOCKED)
      && pathname.match(/[/](overview|symbol|discover|payment|reports|trade-history|my-account)/i)
      && pathname !== ref.current.path
    ) {
      ref.current.path = pathname;
      navigate(pathname + UI_ROUTE_MOCKED, { replace: true });
    }

    if (!ref.current.appLoaded) return;

    // normal login
    if (
      isAuthenticated
      && isPhoneConfirmed !== undefined
      && !isPhoneConfirmed
      && !isUserStatusForVerification
    ) {
      if (
        !(phoneCode && phoneNumber && isPhoneConfirmed !== undefined)
        && !isUserStatusForVerification
      ) {
        setLoginReady();
        navigate(UI_ROUTES.enrollPhoneVerification);
      }
    } else if (
      isAuthResultPath
      && isCookieValid
      && hasSession
      && !isRedirected
      && isPhoneConfirmed
      && (isUserStatusForVerification || (isUserVerified && currentAccount))
    ) {
      setLoginReady();

      // GTM: Track successful login (fire only once per login session)
      if (!hasTrackedLoginSuccess.current) {
        hasTrackedLoginSuccess.current = true;
        const pushLoginEvent = async () => {
          const hashedEmail = await hashSHA256(userEmail || '');
          TagManager.dataLayer({
            dataLayer: {
              event: GTM_EVENTS.ACCOUNT_LOGIN_SUCCESS,
              event_category: GTM_CATEGORIES.USER_MANAGEMENT,
              event_action: GTM_LOGIN.ACTION,
              event_label: GTM_LOGIN.LABEL,
              client_id: userId,
              user_email_hashed: hashedEmail,
            },
          });
        };
        pushLoginEvent();
      }

      navigate(UI_ROUTES.overview);
    } else if (isAuthenticated && isPhoneConfirmed && isUserStatusBeforeForVerification) {
      if (
        !isEnrollPhoneVerified
        && isExtendedInfoReady
        && isTradingPlanReady
        && !hasPaidPlan
        && !isSkipPlanPayment
      ) {
        // if there is no paid plan this means the user did not pass to the payment step
        setLoginReady();
        navigate(UI_ROUTES.enrollPhoneVerified);
      }
    } else if (!isAuthResultPath && isCookieValid && !isRedirected && isAppLoaded) {
      setRedirected(true);
      setLoginReady();
    }
  }, [
    // eslint-disable-line react-hooks/exhaustive-deps
    isTradingPlanReady,
    isSkipPlanPayment,
    hasPaidPlan,
    isExtendedInfoReady,
    isPhoneConfirmed,
    isUserStatusForVerification,
    isAuthResultPath,
    isCookieValid,
    currentAccount,
    hasSession,
    setLoginReady,
    navigate,
    isAppLoaded,
    isAuthenticated,
    isRedirected,
    pathname,
    search,
  ]);

  useEffect(() => {
    if (
      isLoadedApp
      && pathname !== UI_ROUTES.emailConfirm
      && pathname !== UI_ROUTES.signUp
      && pathname !== UI_ROUTES.baseURL
      && !state
      && pathname === UI_ROUTES.overview
    ) {
      if (
        isAuthenticated
        && isExtendedInfoReady
        && !phoneCode
        && !phoneNumber
        && !isUserStatusForVerification
      ) {
        if (!isPhoneConfirmed && !isUserStatusForVerification) {
          navigate(UI_ROUTES.enrollPhoneVerification);
        } else {
          navigate(UI_ROUTES.overview);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isAuthenticated,
    isExtendedInfoReady,
    isPhoneConfirmed,
    isUserStatusForVerification,
    isLoadedApp,
  ]);

  // This flag is used to display the loader while checking the status of the payment reference (including retries).
  // Meanwhile, the individual_status in the extended info is returned as 103
  const isCheckingPaymentStatus: boolean = Boolean(
    isPaymentSuccessReturn
    && paymentReferenceId
    && paymentProvider
    && !isCardTransferStatusError
    && (isCardTransferStatusPending || !isCardTransferStatusReady),
  );

  // After the successful payment path, the payment status and extended info are requested from BE.
  // This flag is defined to display a loader on the screen until individual_status: 200 is returned from the extended info.
  const isPaymentReferenceAndExtendedInfoReady: boolean = isCardTransferStatusReady
    && lastCardTransferStatus?.payment_reference === paymentReferenceId
    && !isUserVerified;

  if (isCookieSessionCheckPending) {
    return (
      <div style={{ border: 'none' }} className={styles.loader}>
        <Preloader
          isFullScreen
          text={lang.commonMessageConnecting()}
          owner="App"
        />
      </div>
    );
  }

  if (
    isAuthResultPath
    || isLoginPending
    || (isCookieValid && !isAppConnected && !isForceRedirectToEnroll)
    || (isGatewayConnectionTimedOut && !IGNORE_GATEWAY_CONNECTION)
    || emailConfirmationOrResentWhileLoggedIn
    || (MOCK.ENABLED && !isGWConnected)
    || (!MOCK.ENABLED && isMockedPath)
    || (isAuthenticated && !isExtendedInfoReady)
    || (isAuthenticated && isPhoneConfirmed === undefined)
    || isCheckingPaymentStatus
    || isPaymentReferenceAndExtendedInfoReady
    || isActivatingDemoAccount
    || (isActivatedDemoAccount && !isUserVerified)
  ) {
    let message = isLoginPending ? lang.commonMessageLoading() : lang.commonMessageConnecting();
    if (isGatewayConnectionTimedOut) message = lang.commonMessageConnectionTimedOut();
    if (pathname.match(/tradingView/i)) {
      return (
        <div className={styles.loadingIndicator}>
          <div className={styles.spinner} />
        </div>
      );
    }
    return (
      <div style={{ border: 'none' }} className={styles.loader}>
        <Preloader
          isFullScreen
          text={message}
          timeOutSetup={{
            checkIfTimeout: () => !isCookieValid,
            onTimeout: () => {
              appLogOut();
              navigate(UI_ROUTES.signUp);
            },
          }}
          owner="App"
        />
      </div>
    );
  }

  if (!isLoadedApp) {
    setIsLoadedApp(true);
  }

  const redirectTo = (): AuthenticatedRedirect => {
    if (isPaymentPlanErrorPath) {
      // If paymentProvider is available, append as query param
      if (paymentProvider) {
        return { to: preparePaymentErrorPathByPaymentProvider(paymentProvider) };
      }
      return { to: UI_ROUTES.paymentError };
    }

    if (
      isPaymentSuccessReturn
      && (isCardTransferStatusError || (isCardTransferStatusReady && !isPlanPaymentProcessed))
    ) {
      if (paymentProvider) {
        return { to: preparePaymentErrorPathByPaymentProvider(paymentProvider) };
      }
      return { to: UI_ROUTES.paymentError };
    }

    const redirectPathname = (autoLoginSuccessURL || UI_ROUTES.overview) as string;

    // Pass isPlanCallback state if it's a plan purchase return
    if (isPlanPurchaseReturn) {
      return { to: redirectPathname, state: { isPlanCallback: true } };
    }

    return { to: redirectPathname };
  };

  const defaultRedirect = redirectTo();

  return (
    <div
      // style={{
      //   zoom: (browserWidthSize <= ZOOM_BEHAVIOR_WIDTH && !isZoomScreen)
      //     ? `${Math.floor(browserWidthSize / 10).toString()}%`
      //     : '' }}
      className={classNames(isFullScreen ? styles.fullScreenContainer : styles.container, {
        [styles.containerWithSidebar]: !isFullScreen && !isMobile,
      })}
    >
      {isAuthenticated
        && !isErrorPath
        && !isTradingViewPath && (
          <>
            <CloseoutManager />
            <MaintenanceNoticeBar />
            {!isFullScreen && <ModalsManager />}
            {!isFullScreen && <HeaderAndSideBar />}
          </>
      )}

      <div className={isFullScreen ? styles.fullScreenContent : styles.content}>
        <div
          className={classNames({
            [styles.fullScreenContentWrapper]: isFullScreen,
            [styles.contentWrapper]: !isFullScreen && !isMobile,
          })}
        >
          {isAuthenticated ? (
            <>
              <NotificationsManager />
              <ScrollToTop />
              <RoutesRenderer routes={getAuthenticatedRoutes({ redirectTarget: defaultRedirect })} />
            </>
          ) : (
            <RoutesRenderer routes={ANONYMOUS_ROUTES} />
          )}
        </div>
      </div>
      <DebugPanels />
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  isForceRedirectToEnroll: state.auth.isForceRedirectToEnroll,
  isAppLoaded: state.app.loaded,
  isOnline: state.app.isOnline,
  isAppConnected: state.app.connected,
  isLoginPending: storeIsLoginPending(state),
  isCookieValid: hasValidCookie(state),
  isCookieSessionCheckPending: isCookieCheckPending(state),
  isAuthenticated: hasValidCookie(state) && state.app.loaded,
  isPhoneConfirmed: state.crm.individualExtendedInfo?.phone_number_confirmed,
  currentAccount: getCurrentAccount(state),
  isUserVerified: isUserVerifiedStatus(state.crm.individualExtendedInfo),
  isUserStatusForVerification: isUserEnrollStepForVerification(state.crm.individualExtendedInfo),
  isUserStatusBeforeForVerification: isUserBeforeForVerificationStatus(
    state.crm.individualExtendedInfo,
  ),
  isExtendedInfoReady: isCallStatusReady(CRMCall.getIndividualExtendedInfo, state.crm.statusByCall),
  phoneNumber: state.auth.enroll.phoneNumber,
  phoneCode: state.auth.enroll.phoneCode,
  autoLoginSuccessURL: state.auth.autoLoginSuccessURL,
  hasPaidPlan: hasAnyAccountPaidPlan(state.crm),
  isTradingPlanReady: isCallStatusReady(CRMCall.searchTradingPlans, state.crm.statusByCall),
  isRedeemSignupTokenFlow: state.auth.enroll.lastType === ENROLL_AUTH_REQUEST_TYPE.REDEEM_SIGNUP_TOKEN,
  isCardTransferStatusReady: isCallStatusReady(
    PaymentCall.getCardTransferStatus,
    state.payment.statusByCall,
  ),
  isCardTransferStatusPending: isCallStatusPending(
    PaymentCall.getCardTransferStatus,
    state.payment.statusByCall,
  ),
  isCardTransferStatusError: isCallStatusError(
    PaymentCall.getCardTransferStatus,
    state.payment.statusByCall,
  ),
  isCardTransferStatusInitial: isCallStatusInitial(
    PaymentCall.getCardTransferStatus,
    state.payment.statusByCall,
  ),
  isActivatingDemoAccount: isCallStatusPending(CRMCall.activateDemoAccount, state.crm.statusByCall),
  isActivatedDemoAccount: isCallStatusReady(CRMCall.activateDemoAccount, state.crm.statusByCall),
  lastCardTransferStatus: state.payment.lastCardTransfer.statusCheckData,
  isPlanPaymentProcessed: isTradingPlanPaymentProcessed(state.payment),
  userId: state.auth.enroll.userId,
  userEmail: state.crm.individualExtendedInfo?.email,
});

const mapDispatchToProps = (dispatch: Dispatch<Action>) => ({
  getPlanPaymentStatus: (param: CardTransferStatusSuccessParam) =>
    dispatch(getCardTransferStatus(param)),
  loginRedirect: () => {
    dispatch(login({ authType: 'login' }));
  },
  start: () => dispatch(applicationStartup()),
  notifyGatewayConnected: () => dispatch(gatewayConnected()),
  notifyAppConnected: () => dispatch(appConnected()),
  appLogOut: (payload?: Parameters<typeof logout>[0]) => dispatch(logout(payload)),
  loginResultCallback: () => dispatch(login({ authType: 'callback' })),
  setLoginReady: () => dispatch(setWebLoginReady()),
  reconnectToGwAndSubscibeSymbols: () => dispatch(reconnectEventAction('App')),
  getAllDataMessages: () => dispatch(getAllMessages()),
  setNetworkStatus: (hasConnection: boolean) => dispatch(setOnlineStatus(hasConnection)),
  setPurchasedPlanId: (planId: number | null) => dispatch(setPendingPurchasedPlanId(planId)),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(App);
